// src/pages/ControleFicha.js — Controle de Ficha de Pesagem
// Fonte: /api/producao/controle-ficha (Firebird FC12100 + FC1A000 Monitoramento
// de Pesagem + SQL Server REQUISICOES/filiais).
// Mesmos filtros da tela de Produção; divide as requisições em duas colunas:
//   - Impressos     → têm registro de pesagem em FC1A000 (ficha impressa/pesada)
//   - Não impressos → sem registro de pesagem
import React, { useState, useEffect, useCallback, useMemo, useDeferredValue, startTransition } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Chip, Stack,
  CircularProgress, Alert, IconButton, Tooltip, MenuItem, Select, FormControl
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon, Inventory2 as InventoryIcon,
  Storefront as StoreIcon, Science as LabIcon,
  CheckCircle as CheckIcon, Clear as ClearIcon,
  Refresh as RefreshIcon, Search as SearchIcon,
  Print as PrintIcon, PrintDisabled as PrintDisabledIcon,
  Medication as MedicationIcon
} from '@mui/icons-material';
import { FixedSizeList } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import { useAuth } from '../contexts/AuthContext';

const EMPTY_ARRAY = Object.freeze([]);
const CARD_SLOT_HEIGHT = 188;

const COLUNAS_KEYS = ['impressos', 'nao_impressos'];
const EMPTY_DADOS = Object.freeze(
  COLUNAS_KEYS.reduce((acc, k) => { acc[k] = EMPTY_ARRAY; return acc; }, {})
);

const COLUNAS = [
  { key: 'impressos',     titulo: 'Impressos',     tituloLong: 'Fichas de Pesagem Impressas',     icon: <PrintIcon fontSize="small" />,         headerBg: '#dcfce7', headerColor: '#15803d', accent: '#22c55e' },
  { key: 'nao_impressos', titulo: 'Não impressos', tituloLong: 'Fichas de Pesagem Não Impressas', icon: <PrintDisabledIcon fontSize="small" />, headerBg: '#fee2e2', headerColor: '#b91c1c', accent: '#ef4444' }
];

// De-para oficial dos códigos TPFORMAFARMA do ERP.
const FORMA_FARMA_NOMES = {
  1: 'Cápsula', 2: 'Creme', 3: 'Loção', 4: 'Shampoo', 5: 'Outras', 6: 'Unidades',
  7: 'Homeopatia', 8: 'Floral', 9: 'Comprimido', 10: 'Gel', 11: 'Pomada', 12: 'Xarope',
  13: 'Filtro Solar', 14: 'Injetável', 15: 'Envelope', 16: 'Biscoito Medicamentoso',
  17: 'Pastilha Medicamentosa', 18: 'Patch Gel', 19: 'Filmes', 20: 'Pasta Oral',
  21: 'Solução Oral'
};

const STATUS_TONE = {
  info:    { bg: '#dbeafe', color: '#1d4ed8', icon: <LabIcon sx={{ fontSize: 14 }} /> },
  success: { bg: '#dcfce7', color: '#15803d', icon: <CheckIcon sx={{ fontSize: 14 }} /> }
};

const formatDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('pt-BR');
};

// Combina DTRET/DTPES (data) + hora ("HH:MM:SS") num único timestamp para exibição.
const formatDataHora = (dataIso, hora) => {
  if (!dataIso) return null;
  const d = new Date(dataIso);
  if (Number.isNaN(d.getTime())) return null;
  let hh = 0, mm = 0;
  if (hora) {
    const m = String(hora).match(/(\d{1,2}):(\d{2})(?::(\d{2}))?/);
    if (m) { hh = parseInt(m[1], 10); mm = parseInt(m[2], 10); }
  }
  const dia = String(d.getDate()).padStart(2, '0');
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const ano = d.getFullYear();
  const horaTxt = hora ? ` ${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}` : '';
  return `${dia}/${mes}/${ano}${horaTxt}`;
};

// Decodifica strings duplo-encodadas em UTF-8 (Firebird WIN1252→UTF8 de novo)
const fixEncoding = (s) => {
  if (!s) return s;
  try { return decodeURIComponent(escape(s)); } catch (_) { return s; }
};

const cardFromApi = (it) => {
  const cdfil = String(it.cdfil ?? '');
  const nrrqu = String(it.nrrqu ?? '');
  const serier = String(it.serier ?? '');
  return {
    codigo: `${it.cdfil}-${it.nrrqu}-${it.serier}`,
    filialCod: String(it.cdfile ?? '').padStart(3, '0'),
    filial: fixEncoding(it.filial_estoque_nome) || '',
    destinoCod: String(it.cdfild ?? '').padStart(3, '0'),
    destino: fixEncoding(it.filial_destino_nome) || '',
    serie: it.serier,
    formaNome: FORMA_FARMA_NOMES[it.tpformafarma] || (it.tpformafarma != null ? String(it.tpformafarma) : ''),
    entrada: formatDate(it.dtentr),
    retirada: formatDataHora(it.dtret, it.hrret),
    impressa: !!it.impressa,
    qtdImpressoes: it.qtd_impressoes || 0,
    _searchKey: `${cdfil}${nrrqu}${serier}|${cdfil}-${nrrqu}-${serier}`.toLowerCase(),
    raw: it
  };
};

const FilialChip = React.memo(({ codigo, nome }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
    <Box sx={{
      bgcolor: '#f1f5f9', color: '#475569', fontWeight: 700,
      px: 0.75, py: 0.125, borderRadius: 0.75,
      fontSize: { xs: 12, md: 10.5, lg: 11.5 }, lineHeight: 1.5, flexShrink: 0
    }}>{codigo || '—'}</Box>
    {nome && (
      <Typography
        sx={{ fontSize: { xs: 12, md: 10.5, lg: 11.5 }, color: '#64748b', fontWeight: 500, minWidth: 0 }}
        noWrap title={nome}
      >{nome}</Typography>
    )}
  </Box>
));

const StatusPill = React.memo(({ tone, label, when }) => {
  const t = STATUS_TONE[tone] || STATUS_TONE.info;
  return (
    <Box sx={{
      mt: 1, p: { xs: 0.75, md: 0.5 }, borderRadius: 1,
      border: `1px dashed ${t.color}55`, bgcolor: t.bg,
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25, minWidth: 0
    }}>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 0.5,
        color: t.color, fontWeight: 600, fontSize: { xs: 12, md: 10, lg: 11 },
        textAlign: 'center', minWidth: 0
      }}>
        {t.icon}<span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
      </Box>
      {when && (
        <Typography sx={{ fontSize: { xs: 10.5, md: 9, lg: 10 }, color: t.color, opacity: 0.85 }}>
          ({when})
        </Typography>
      )}
    </Box>
  );
});

const RequisicaoCard = React.memo(({ item, accent }) => (
  <Paper
    elevation={0}
    sx={{
      p: { xs: 1.25, md: 0.75, lg: 1 }, mb: 0.75, borderRadius: 1.5,
      border: '1px solid #e2e8f0', borderLeft: `3px solid ${accent}`,
      bgcolor: '#fff', transition: 'all .15s ease', minWidth: 0,
      '&:hover': { boxShadow: '0 2px 6px rgba(0,0,0,.08)', transform: 'translateY(-1px)' }
    }}
  >
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5, gap: 0.5, minWidth: 0 }}>
      <Typography
        sx={{
          fontFamily: 'monospace', fontWeight: 700,
          fontSize: { xs: 13, md: 11.5, lg: 12.5 }, color: '#0f172a',
          minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
        }}
        title={item.codigo}
      >{item.codigo}</Typography>
      {item.formaNome && (
        <Chip
          size="small"
          icon={<MedicationIcon sx={{ fontSize: 12 }} />}
          label={item.formaNome}
          sx={{
            height: { xs: 18, md: 16 }, fontSize: { xs: 10, md: 9 }, fontWeight: 700,
            bgcolor: '#eef2ff', color: '#4338ca',
            '& .MuiChip-icon': { color: '#4338ca', ml: 0.5 },
            '& .MuiChip-label': { px: 0.6 }, flexShrink: 0
          }}
        />
      )}
    </Box>

    <FilialChip codigo={item.filialCod} nome={item.filial} />

    {item.destino && (
      <Typography sx={{ fontSize: { xs: 11.5, md: 10, lg: 11 }, color: '#64748b', mt: 0.25 }} noWrap title={item.destino}>
        → <strong style={{ color: '#0f172a' }}>{item.destinoCod}</strong> {item.destino}
      </Typography>
    )}

    {item.entrada && (
      <Typography sx={{ fontSize: { xs: 12, md: 10.5, lg: 11.5 }, color: '#64748b', mt: 0.25 }}>
        Entrada: <strong style={{ color: '#0f172a' }}>{item.entrada}</strong>
      </Typography>
    )}
    {item.retirada && (
      <Typography sx={{ fontSize: { xs: 12, md: 10.5, lg: 11.5 }, color: '#64748b' }}>
        Retirada: <strong style={{ color: '#0f172a' }}>{item.retirada}</strong>
      </Typography>
    )}

    {item.impressa
      ? <StatusPill tone="success" label="Ficha impressa" when={item.qtdImpressoes > 1 ? `${item.qtdImpressoes}x impressões` : null} />
      : <StatusPill tone="info" label="Ficha não impressa" />}
  </Paper>
));

const ColunaVazia = React.memo(({ titulo }) => (
  <Box sx={{
    p: 2, textAlign: 'center', color: '#94a3b8', bgcolor: '#f8fafc',
    border: '1px dashed #cbd5e1', borderRadius: 1.5, fontSize: 12.5
  }}>
    📭 Nenhuma requisição — {titulo.toLowerCase()}
  </Box>
));

const VirtualRow = React.memo(({ index, style, data }) => {
  const item = data.items[index];
  return (
    <div style={style}>
      <RequisicaoCard item={item} accent={data.accent} />
    </div>
  );
}, (prev, next) =>
  prev.index === next.index &&
  prev.data === next.data &&
  prev.style.top === next.style.top
);

const ColunaFicha = React.memo(({ col, itens, loading }) => {
  const itemData = useMemo(() => ({ items: itens, accent: col.accent }), [itens, col.accent]);
  return (
    <Paper
      elevation={0}
      sx={{
        bgcolor: '#fff', borderRadius: 2, border: '1px solid #e2e8f0',
        borderTop: `3px solid ${col.accent}`, display: 'flex', flexDirection: 'column',
        height: 'calc(100vh - 180px)', minWidth: 0
      }}
    >
      <Box sx={{
        px: { xs: 1.25, md: 1 }, py: { xs: 0.75, md: 0.6 },
        borderBottom: '1px solid #e2e8f0',
        bgcolor: col.headerBg, color: col.headerColor,
        borderTopLeftRadius: 8, borderTopRightRadius: 8,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 0.5,
        flexShrink: 0
      }}>
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0, flex: 1 }}>
          {col.icon}
          <Typography sx={{ fontSize: { xs: 14, md: 13 }, fontWeight: 700, lineHeight: 1.2, minWidth: 0 }} noWrap title={col.tituloLong}>
            {col.titulo}
          </Typography>
        </Stack>
        <Chip
          label={itens.length}
          size="small"
          sx={{
            height: 22, fontWeight: 700, fontSize: 12,
            bgcolor: '#fff', color: col.headerColor,
            border: `1px solid ${col.headerColor}33`, flexShrink: 0,
            '& .MuiChip-label': { px: 0.8 }
          }}
        />
      </Box>
      {loading ? (
        <Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress size={20} /></Box>
      ) : itens.length === 0 ? (
        <Box sx={{ p: { xs: 1, md: 0.75 } }}>
          <ColunaVazia titulo={col.tituloLong} />
        </Box>
      ) : (
        <Box sx={{ flex: 1, minWidth: 0, px: { xs: 1, md: 0.75 }, pt: { xs: 1, md: 0.75 } }}>
          <AutoSizer>
            {({ height, width }) => (
              <FixedSizeList
                height={height} width={width}
                itemCount={itens.length} itemSize={CARD_SLOT_HEIGHT}
                itemData={itemData} overscanCount={4}
              >
                {VirtualRow}
              </FixedSizeList>
            )}
          </AutoSizer>
        </Box>
      )}
    </Paper>
  );
});

const SearchInput = React.memo(({ onChange }) => {
  const [local, setLocal] = useState('');
  return (
    <TextField
      size="small" variant="standard"
      value={local}
      onChange={(e) => {
        const v = e.target.value;
        setLocal(v);
        startTransition(() => onChange(v));
      }}
      placeholder="código ou nº requisição"
      InputProps={{ disableUnderline: true, sx: { fontSize: 12.5, fontWeight: 600 } }}
      fullWidth
    />
  );
});

const FiltroCampo = ({ icon, label, children, dimmed, minWidth = 170 }) => (
  <Paper elevation={0} sx={{
    px: 1, py: 0.25, borderRadius: 999, border: '1px solid #e2e8f0',
    display: 'flex', alignItems: 'center', gap: 0.75, minWidth, bgcolor: dimmed ? '#f1f5f9' : '#fff',
    opacity: dimmed ? 0.6 : 1
  }}>
    <Box sx={{ color: '#64748b', display: 'flex' }}>{icon}</Box>
    <Typography sx={{ fontSize: 11.5, color: '#475569', whiteSpace: 'nowrap' }}>{label}:</Typography>
    <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
  </Paper>
);

const hojeISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const ControleFicha = () => {
  const { api } = useAuth();

  const [dataEntrada, setDataEntrada]   = useState(hojeISO());
  const [dataRetirada, setDataRetirada] = useState('');
  const [horaInicio, setHoraInicio] = useState('');
  const [horaFim, setHoraFim]       = useState('');
  const [filialEstoque, setFilialEstoque] = useState('');
  const [filialDestino, setFilialDestino] = useState('');
  const [filialOrigem, setFilialOrigem] = useState('');
  const [formaFarma, setFormaFarma] = useState('');
  const [statusLab, setStatusLab] = useState('');
  const [busca, setBusca] = useState('');
  const [buscaResetKey, setBuscaResetKey] = useState(0);

  const [filiais, setFiliais] = useState([]);
  const [formasFarma, setFormasFarma] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [colunasApi, setColunasApi] = useState(null);

  useEffect(() => {
    api.listarFiliais()
      .then(resp => setFiliais(resp?.dados || resp?.data || []))
      .catch(() => {});
  }, [api]);

  useEffect(() => {
    api.listarFormasFarma()
      .then(resp => setFormasFarma(resp?.dados || []))
      .catch(() => {});
  }, [api]);

  const carregar = useCallback(async () => {
    if (!dataEntrada && !dataRetirada) {
      setError('Informe Data de Entrada OU Data de Retirada.');
      setColunasApi(null);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const filtros = {};
      if (dataEntrada)   filtros.dtentr = dataEntrada;
      else               filtros.dtret  = dataRetirada;
      if (filialEstoque) filtros.cdfile = filialEstoque;
      if (filialDestino) filtros.cdfild = filialDestino;
      if (filialOrigem)  filtros.cdfilo = filialOrigem;
      if (formaFarma)    filtros.tpformafarma = formaFarma;
      if (dataRetirada && horaInicio) filtros.hrret_inicio = horaInicio;
      if (dataRetirada && horaFim)    filtros.hrret_fim    = horaFim;

      const resp = await api.controleFicha(filtros);
      if (resp?.success) {
        startTransition(() => setColunasApi(resp.colunas || null));
      } else {
        setError(resp?.message || 'Erro ao carregar Controle de Ficha');
        setColunasApi(null);
      }
    } catch (e) {
      setError(e.message || 'Falha de conexão com a API');
      setColunasApi(null);
    } finally {
      setLoading(false);
    }
  }, [api, dataEntrada, dataRetirada, filialEstoque, filialDestino, filialOrigem, formaFarma, horaInicio, horaFim]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { carregar(); }, []);

  const limpar = () => {
    setDataEntrada(hojeISO());
    setDataRetirada('');
    setHoraInicio('');
    setHoraFim('');
    setFilialEstoque('');
    setFilialDestino('');
    setFilialOrigem('');
    setFormaFarma('');
    setStatusLab('');
    setBusca('');
    setBuscaResetKey(k => k + 1);
  };

  const handleEntradaChange = (v) => {
    setDataEntrada(v);
    if (v) { setDataRetirada(''); setHoraInicio(''); setHoraFim(''); }
  };
  const handleRetiradaChange = (v) => {
    setDataRetirada(v);
    if (v) setDataEntrada('');
  };
  const handleEntradaFocus = () => {
    if (dataRetirada) setDataRetirada('');
    if (horaInicio) setHoraInicio('');
    if (horaFim) setHoraFim('');
  };
  const handleRetiradaFocus = () => {
    if (dataEntrada) setDataEntrada('');
  };

  const cardsPorColuna = useMemo(() => {
    if (!colunasApi) return null;
    const out = {};
    for (const col of COLUNAS) {
      out[col.key] = (colunasApi[col.key] || []).map(cardFromApi);
    }
    return out;
  }, [colunasApi]);

  const buscaDeferred = useDeferredValue(busca);
  const statusLabDeferred = useDeferredValue(statusLab);

  const dadosPorColuna = useMemo(() => {
    if (!cardsPorColuna) return EMPTY_DADOS;
    const termo = buscaDeferred.trim().toLowerCase();
    if (!termo && !statusLabDeferred) return cardsPorColuna;
    const out = {};
    for (const col of COLUNAS) {
      const src = cardsPorColuna[col.key] || EMPTY_ARRAY;
      if (src.length === 0) { out[col.key] = EMPTY_ARRAY; continue; }
      const filtered = src.filter(c => {
        if (statusLabDeferred && c.raw.setor_lab !== statusLabDeferred) return false;
        if (termo && !c._searchKey.includes(termo)) return false;
        return true;
      });
      if (filtered.length === src.length) out[col.key] = src;
      else if (filtered.length === 0) out[col.key] = EMPTY_ARRAY;
      else out[col.key] = filtered;
    }
    return out;
  }, [cardsPorColuna, buscaDeferred, statusLabDeferred]);

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', p: 2 }}>
      {/* Filtros topo (idênticos à tela de Produção) */}
      <Paper elevation={0} sx={{
        p: 1, mb: 2, borderRadius: 2, border: '1px solid #e2e8f0',
        display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', justifyContent: 'center'
      }}>
        <FiltroCampo icon={<SearchIcon fontSize="small" />} label="Buscar" minWidth={180}>
          <SearchInput key={buscaResetKey} onChange={setBusca} />
        </FiltroCampo>

        <FiltroCampo icon={<CalendarIcon fontSize="small" />} label="Entrada" dimmed={!!dataRetirada} minWidth={155}>
          <TextField
            type="date" size="small" variant="standard"
            value={dataEntrada}
            onFocus={handleEntradaFocus}
            onChange={(e) => handleEntradaChange(e.target.value)}
            InputProps={{ disableUnderline: true, sx: { fontSize: 13, fontWeight: 600 } }}
            fullWidth
          />
        </FiltroCampo>

        <FiltroCampo icon={<CalendarIcon fontSize="small" />} label="Retirada" dimmed={!!dataEntrada} minWidth={155}>
          <TextField
            type="date" size="small" variant="standard"
            value={dataRetirada}
            onFocus={handleRetiradaFocus}
            onChange={(e) => handleRetiradaChange(e.target.value)}
            InputProps={{ disableUnderline: true, sx: { fontSize: 13, fontWeight: 600 } }}
            fullWidth
          />
        </FiltroCampo>

        <FiltroCampo icon={<CalendarIcon fontSize="small" />} label="Hora ini" dimmed={!dataRetirada} minWidth={120}>
          <TextField
            type="time" size="small" variant="standard"
            disabled={!dataRetirada}
            value={horaInicio}
            onChange={(e) => setHoraInicio(e.target.value)}
            InputProps={{ disableUnderline: true, sx: { fontSize: 13, fontWeight: 600 } }}
            fullWidth
          />
        </FiltroCampo>

        <FiltroCampo icon={<CalendarIcon fontSize="small" />} label="Hora fim" dimmed={!dataRetirada} minWidth={120}>
          <TextField
            type="time" size="small" variant="standard"
            disabled={!dataRetirada}
            value={horaFim}
            onChange={(e) => setHoraFim(e.target.value)}
            InputProps={{ disableUnderline: true, sx: { fontSize: 13, fontWeight: 600 } }}
            fullWidth
          />
        </FiltroCampo>

        <FiltroCampo icon={<StoreIcon fontSize="small" />} label="Origem" minWidth={170}>
          <FormControl size="small" variant="standard" fullWidth>
            <Select value={filialOrigem} onChange={(e) => setFilialOrigem(e.target.value)} disableUnderline displayEmpty sx={{ fontSize: 13, fontWeight: 600 }}>
              <MenuItem value=""><em>Todas</em></MenuItem>
              {filiais.map(f => (
                <MenuItem key={`fo-${f.codigo_filial}`} value={f.codigo_filial}>
                  {String(f.codigo_filial).padStart(3, '0')} {f.nome_filial}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </FiltroCampo>

        <FiltroCampo icon={<StoreIcon fontSize="small" />} label="Estoque" minWidth={170}>
          <FormControl size="small" variant="standard" fullWidth>
            <Select value={filialEstoque} onChange={(e) => setFilialEstoque(e.target.value)} disableUnderline displayEmpty sx={{ fontSize: 13, fontWeight: 600 }}>
              <MenuItem value=""><em>Todas</em></MenuItem>
              {filiais.map(f => (
                <MenuItem key={`fe-${f.codigo_filial}`} value={f.codigo_filial}>
                  {String(f.codigo_filial).padStart(3, '0')} {f.nome_filial}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </FiltroCampo>

        <FiltroCampo icon={<InventoryIcon fontSize="small" />} label="Destino" minWidth={170}>
          <FormControl size="small" variant="standard" fullWidth>
            <Select value={filialDestino} onChange={(e) => setFilialDestino(e.target.value)} disableUnderline displayEmpty sx={{ fontSize: 13, fontWeight: 600 }}>
              <MenuItem value=""><em>Todas</em></MenuItem>
              {filiais.map(f => (
                <MenuItem key={`fd-${f.codigo_filial}`} value={f.codigo_filial}>
                  {String(f.codigo_filial).padStart(3, '0')} {f.nome_filial}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </FiltroCampo>

        <FiltroCampo icon={<MedicationIcon fontSize="small" />} label="Forma" minWidth={200}>
          <FormControl size="small" variant="standard" fullWidth>
            <Select value={formaFarma} onChange={(e) => setFormaFarma(e.target.value)} disableUnderline displayEmpty sx={{ fontSize: 13, fontWeight: 600 }}>
              <MenuItem value=""><em>Todas</em></MenuItem>
              {formasFarma.map(ff => (
                <MenuItem key={`ff-${ff.codigo}`} value={ff.codigo}>
                  {String(ff.codigo).padStart(2, '0')}
                  {FORMA_FARMA_NOMES[ff.codigo] ? ` ${FORMA_FARMA_NOMES[ff.codigo]}` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </FiltroCampo>

        <FiltroCampo icon={<LabIcon fontSize="small" />} label="Status" minWidth={170}>
          <FormControl size="small" variant="standard" fullWidth>
            <Select
              value={statusLab}
              onChange={(e) => {
                const val = e.target.value;
                startTransition(() => setStatusLab(val));
              }}
              disableUnderline displayEmpty sx={{ fontSize: 13, fontWeight: 600 }}
            >
              <MenuItem value=""><em>Todos</em></MenuItem>
              <MenuItem value="visto_farmac">Visto Farmacêutico</MenuItem>
              <MenuItem value="ordem_prod">Ordem de Produção</MenuItem>
              <MenuItem value="pesagem">Pesagem</MenuItem>
              <MenuItem value="tritura">Trituração</MenuItem>
              <MenuItem value="encap">Encapsulação</MenuItem>
              <MenuItem value="peso_medio">Peso Médio</MenuItem>
              <MenuItem value="sache">Sachê</MenuItem>
              <MenuItem value="dermato">Dermato</MenuItem>
              <MenuItem value="xarope">Xarope</MenuItem>
              <MenuItem value="homeopat">Homeopatia</MenuItem>
              <MenuItem value="cosmet">Cosmético</MenuItem>
              <MenuItem value="conferenc">Conferência / Liberação</MenuItem>
              <MenuItem value="outro">Outro</MenuItem>
            </Select>
          </FormControl>
        </FiltroCampo>

        <Button
          variant="contained" color="primary" size="small"
          onClick={carregar} disabled={loading}
          sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 600, px: 1.5, fontSize: 12.5 }}
        >
          {loading ? <CircularProgress size={16} color="inherit" /> : 'Aplicar'}
        </Button>
        <Button
          variant="contained" color="error" size="small" startIcon={<ClearIcon />}
          sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 600, px: 1.5, fontSize: 12.5 }}
          onClick={limpar}
        >
          Limpar
        </Button>
        <Tooltip title="Atualizar">
          <span>
            <IconButton size="small" onClick={carregar} disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Duas colunas: Impressos / Não impressos */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
        gap: { xs: 1, md: 1.5 },
        pb: 1
      }}>
        {COLUNAS.map(col => (
          <ColunaFicha
            key={col.key}
            col={col}
            itens={dadosPorColuna[col.key] || EMPTY_ARRAY}
            loading={loading}
          />
        ))}
      </Box>
    </Box>
  );
};

export default ControleFicha;
